import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UsersIcon, DollarSignIcon, BoxIcon, GavelIcon, ClockIcon, ArrowLeftIcon, ChevronRightIcon, ShieldIcon, AlertCircleIcon, CheckCircleIcon, InfoIcon, CreditCardIcon, WalletIcon, LockIcon, CalculatorIcon, PercentIcon } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
const SyndicateContribution: React.FC = () => {
  const navigate = useNavigate();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [contributionAmount, setContributionAmount] = useState('5000');
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [currentStep, setCurrentStep] = useState(1);
  // Mock data for demonstration
  const syndicateData = {
    id: id || 'syn-2023-001',
    name: 'Premium iPhone Collective',
    totalCapital: 90000,
    targetCapital: 100000,
    minimumContribution: 5000,
    status: 'open',
    type: 'auction',
    endDate: '2023-07-25',
    allocation: [{
      type: 'iPhone 13 Pro (256GB)',
      percentage: 40
    }, {
      type: 'iPhone 13 Pro (128GB)',
      percentage: 30
    }, {
      type: 'iPhone 12 Pro Max (256GB)',
      percentage: 20
    }, {
      type: 'iPhone 12 Pro Max (128GB)',
      percentage: 10
    }]
  };
  const formatCurrency = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };
  const handleContributionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setContributionAmount(value);
  };
  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };
  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };
  const handleSubmit = () => {
    // In a real app, this would submit the payment and contribution to an API
    navigate(`/syndicate-detail/${id}`);
  };
  const calculateSharePercentage = () => {
    const contribution = parseFloat(contributionAmount) || 0;
    const totalAfterContribution = syndicateData.totalCapital + contribution;
    return (contribution / totalAfterContribution * 100).toFixed(2);
  };
  const calculateAllocation = (devicePercentage: number) => {
    const contribution = parseFloat(contributionAmount) || 0;
    const share = contribution / syndicateData.targetCapital;
    return (devicePercentage * share).toFixed(2);
  };
  const calculateFees = () => {
    const contribution = parseFloat(contributionAmount) || 0;
    const platformFee = contribution * 0.025;
    const escrowFee = contribution * 0.01;
    return {
      platformFee,
      escrowFee,
      total: platformFee + escrowFee
    };
  };
  return <div className="space-y-6" data-id="element-1989">
      <div className="flex items-center justify-between" data-id="element-1990">
        <div className="flex items-center" data-id="element-1991">
          <Button variant="ghost" size="sm" className="mr-2" onClick={() => navigate(`/syndicate-detail/${id}`)} data-id="element-1992">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-1993" />
            Back to Syndicate
          </Button>
          <h1 className="text-2xl font-bold" data-id="element-1994">Contribute to Syndicate</h1>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1995">
        <div className="md:col-span-2 space-y-6" data-id="element-1996">
          <Card data-id="element-1997">
            <CardHeader data-id="element-1998">
              <CardTitle data-id="element-1999">
                {currentStep === 1 ? 'Step 1: Contribution Amount' : currentStep === 2 ? 'Step 2: Payment Method' : 'Step 3: Review & Confirm'}
              </CardTitle>
              <CardDescription data-id="element-2000">
                {currentStep === 1 ? 'Specify how much you want to contribute to this syndicate' : currentStep === 2 ? 'Select your preferred payment method' : 'Review your contribution details and confirm'}
              </CardDescription>
            </CardHeader>
            <CardContent data-id="element-2001">
              {currentStep === 1 && <div className="space-y-6" data-id="element-2002">
                  <div className="bg-muted/30 p-4 rounded-md border flex items-start" data-id="element-2003">
                    <InfoIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-2004" />
                    <div data-id="element-2005">
                      <p className="text-sm font-medium" data-id="element-2006">
                        Contribution Information
                      </p>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-2007">
                        Your contribution will be held in a secure escrow
                        account until the syndicate reaches its target capital
                        or the end date is reached. You will receive a share of
                        the acquired inventory proportional to your
                        contribution.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-2008">
                    <div data-id="element-2009">
                      <label htmlFor="contribution-amount" className="text-sm font-medium" data-id="element-2010">
                        Contribution Amount
                      </label>
                      <div className="mt-1 relative" data-id="element-2011">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-2012">
                          $
                        </span>
                        <input id="contribution-amount" type="text" className="w-full pl-8 p-3 rounded-md border bg-background text-xl font-bold" value={contributionAmount} onChange={handleContributionChange} data-id="element-2013" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1" data-id="element-2014">
                        Minimum contribution:{' '}
                        {formatCurrency(syndicateData.minimumContribution)}
                      </p>
                    </div>
                    <div className="grid grid-cols-4 gap-2" data-id="element-2015">
                      <Button variant="outline" className="p-2 h-auto" onClick={() => setContributionAmount('5000')} data-id="element-2016">
                        $5,000
                      </Button>
                      <Button variant="outline" className="p-2 h-auto" onClick={() => setContributionAmount('10000')} data-id="element-2017">
                        $10,000
                      </Button>
                      <Button variant="outline" className="p-2 h-auto" onClick={() => setContributionAmount('25000')} data-id="element-2018">
                        $25,000
                      </Button>
                      <Button variant="outline" className="p-2 h-auto" onClick={() => setContributionAmount('50000')} data-id="element-2019">
                        $50,000
                      </Button>
                    </div>
                    {parseFloat(contributionAmount) < syndicateData.minimumContribution && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3 rounded-md text-sm flex items-center" data-id="element-2020">
                        <AlertCircleIcon className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" data-id="element-2021" />
                        <span data-id="element-2022">
                          Contribution must be at least{' '}
                          {formatCurrency(syndicateData.minimumContribution)}
                        </span>
                      </div>}
                  </div>
                </div>}
              {currentStep === 2 && <div className="space-y-6" data-id="element-2023">
                  <div className="bg-muted/30 p-4 rounded-md border flex items-start" data-id="element-2024">
                    <LockIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-2025" />
                    <div data-id="element-2026">
                      <p className="text-sm font-medium" data-id="element-2027">Secure Payment</p>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-2028">
                        All payments are processed securely and held in escrow
                        until the syndicate reaches its target.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-2029">
                    <label className="text-sm font-medium" data-id="element-2030">
                      Select Payment Method
                    </label>
                    <div className={`p-4 border rounded-md cursor-pointer transition-all ${paymentMethod === 'credit_card' ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`} onClick={() => setPaymentMethod('credit_card')} data-id="element-2031">
                      <div className="flex items-center" data-id="element-2032">
                        <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3" data-id="element-2033">
                          <CreditCardIcon className="h-5 w-5" data-id="element-2034" />
                        </div>
                        <div className="flex-1" data-id="element-2035">
                          <p className="font-medium" data-id="element-2036">Credit or Debit Card</p>
                          <p className="text-sm text-muted-foreground" data-id="element-2037">
                            Pay with Visa, Mastercard, or American Express
                          </p>
                        </div>
                        <div className="h-5 w-5 rounded-full border flex items-center justify-center" data-id="element-2038">
                          {paymentMethod === 'credit_card' && <div className="h-3 w-3 rounded-full bg-primary" data-id="element-2039"></div>}
                        </div>
                      </div>
                    </div>
                    <div className={`p-4 border rounded-md cursor-pointer transition-all ${paymentMethod === 'bank_transfer' ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`} onClick={() => setPaymentMethod('bank_transfer')} data-id="element-2040">
                      <div className="flex items-center" data-id="element-2041">
                        <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3" data-id="element-2042">
                          <div className="h-5 w-5" data-id="element-2043" />
                        </div>
                        <div className="flex-1" data-id="element-2044">
                          <p className="font-medium" data-id="element-2045">Bank Transfer (ACH)</p>
                          <p className="text-sm text-muted-foreground" data-id="element-2046">
                            Direct transfer from your bank account
                          </p>
                        </div>
                        <div className="h-5 w-5 rounded-full border flex items-center justify-center" data-id="element-2047">
                          {paymentMethod === 'bank_transfer' && <div className="h-3 w-3 rounded-full bg-primary" data-id="element-2048"></div>}
                        </div>
                      </div>
                    </div>
                    <div className={`p-4 border rounded-md cursor-pointer transition-all ${paymentMethod === 'wire_transfer' ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'}`} onClick={() => setPaymentMethod('wire_transfer')} data-id="element-2049">
                      <div className="flex items-center" data-id="element-2050">
                        <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3" data-id="element-2051">
                          <WalletIcon className="h-5 w-5" data-id="element-2052" />
                        </div>
                        <div className="flex-1" data-id="element-2053">
                          <p className="font-medium" data-id="element-2054">Wire Transfer</p>
                          <p className="text-sm text-muted-foreground" data-id="element-2055">
                            International wire transfer (may take 1-3 business
                            days)
                          </p>
                        </div>
                        <div className="h-5 w-5 rounded-full border flex items-center justify-center" data-id="element-2056">
                          {paymentMethod === 'wire_transfer' && <div className="h-3 w-3 rounded-full bg-primary" data-id="element-2057"></div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
              {currentStep === 3 && <div className="space-y-6" data-id="element-2058">
                  <div className="bg-muted/30 p-4 rounded-md border flex items-start" data-id="element-2059">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-2060" />
                    <div data-id="element-2061">
                      <p className="text-sm font-medium" data-id="element-2062">Ready to Contribute</p>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-2063">
                        Please review your contribution details below and
                        confirm to complete the transaction.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-2064">
                    <h3 className="text-sm font-medium" data-id="element-2065">
                      Contribution Summary
                    </h3>
                    <div className="p-4 border rounded-md space-y-4" data-id="element-2066">
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-2067">
                        <span className="text-sm text-muted-foreground" data-id="element-2068">
                          Syndicate
                        </span>
                        <span className="font-medium" data-id="element-2069">
                          {syndicateData.name}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-2070">
                        <span className="text-sm text-muted-foreground" data-id="element-2071">
                          Contribution Amount
                        </span>
                        <span className="font-medium" data-id="element-2072">
                          {formatCurrency(contributionAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-2073">
                        <span className="text-sm text-muted-foreground" data-id="element-2074">
                          Payment Method
                        </span>
                        <span className="font-medium" data-id="element-2075">
                          {paymentMethod === 'credit_card' ? 'Credit/Debit Card' : paymentMethod === 'bank_transfer' ? 'Bank Transfer (ACH)' : 'Wire Transfer'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-2076">
                        <div className="flex items-center" data-id="element-2077">
                          <span className="text-sm text-muted-foreground" data-id="element-2078">
                            Platform Fee (2.5%)
                          </span>
                          <TooltipProvider data-id="element-2079">
                            <Tooltip data-id="element-2080">
                              <TooltipTrigger asChild data-id="element-2081">
                                <InfoIcon className="h-3 w-3 ml-1 text-muted-foreground" data-id="element-2082" />
                              </TooltipTrigger>
                              <TooltipContent data-id="element-2083">
                                <p data-id="element-2084">Fee for using the platform services</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <span className="font-medium" data-id="element-2085">
                          {formatCurrency(calculateFees().platformFee)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b" data-id="element-2086">
                        <div className="flex items-center" data-id="element-2087">
                          <span className="text-sm text-muted-foreground" data-id="element-2088">
                            Escrow Fee (1.0%)
                          </span>
                          <TooltipProvider data-id="element-2089">
                            <Tooltip data-id="element-2090">
                              <TooltipTrigger asChild data-id="element-2091">
                                <InfoIcon className="h-3 w-3 ml-1 text-muted-foreground" data-id="element-2092" />
                              </TooltipTrigger>
                              <TooltipContent data-id="element-2093">
                                <p data-id="element-2094">Fee for holding funds in secure escrow</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <span className="font-medium" data-id="element-2095">
                          {formatCurrency(calculateFees().escrowFee)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2" data-id="element-2096">
                        <span className="font-medium" data-id="element-2097">Total Charge</span>
                        <span className="text-lg font-bold" data-id="element-2098">
                          {formatCurrency(parseFloat(contributionAmount) + calculateFees().total)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 mt-4" data-id="element-2099">
                      <div className="h-5 w-5 rounded border flex items-center justify-center" data-id="element-2100">
                        <CheckCircleIcon className="h-4 w-4 text-primary" data-id="element-2101" />
                      </div>
                      <span className="text-sm" data-id="element-2102">
                        I agree to the syndicate terms and conditions
                      </span>
                    </div>
                  </div>
                </div>}
            </CardContent>
            <CardFooter className="border-t pt-6 flex justify-between" data-id="element-2103">
              {currentStep > 1 ? <Button variant="outline" onClick={handlePreviousStep} data-id="element-2104">
                  <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2105" />
                  Back
                </Button> : <Button variant="outline" onClick={() => navigate(`/syndicate-detail/${id}`)} data-id="element-2106">
                  <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2107" />
                  Cancel
                </Button>}
              {currentStep < 3 ? <Button onClick={handleNextStep} disabled={currentStep === 1 && parseFloat(contributionAmount) < syndicateData.minimumContribution} data-id="element-2108">
                  Continue <ChevronRightIcon className="ml-2 h-4 w-4" data-id="element-2109" />
                </Button> : <Button onClick={handleSubmit} data-id="element-2110">
                  Confirm Contribution{' '}
                  <CheckCircleIcon className="ml-2 h-4 w-4" data-id="element-2111" />
                </Button>}
            </CardFooter>
          </Card>
        </div>
        <div className="space-y-6" data-id="element-2112">
          <Card data-id="element-2113">
            <CardHeader data-id="element-2114">
              <CardTitle data-id="element-2115">Syndicate Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-2116">
              <div data-id="element-2117">
                <h3 className="text-sm font-medium mb-2" data-id="element-2118">
                  {syndicateData.name}
                </h3>
                <div className="space-y-1" data-id="element-2119">
                  <div className="flex justify-between text-sm" data-id="element-2120">
                    <span className="text-muted-foreground" data-id="element-2121">
                      Current Capital
                    </span>
                    <span data-id="element-2122">{formatCurrency(syndicateData.totalCapital)}</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-2123">
                    <span className="text-muted-foreground" data-id="element-2124">
                      Target Capital
                    </span>
                    <span data-id="element-2125">{formatCurrency(syndicateData.targetCapital)}</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-2126">
                    <span className="text-muted-foreground" data-id="element-2127">End Date</span>
                    <span data-id="element-2128">{syndicateData.endDate}</span>
                  </div>
                </div>
              </div>
              <div data-id="element-2129">
                <p className="text-sm text-muted-foreground mb-1" data-id="element-2130">
                  Funding Progress
                </p>
                <Progress value={syndicateData.totalCapital / syndicateData.targetCapital * 100} className="h-2" data-id="element-2131" />
                <div className="flex justify-between mt-1 text-xs text-muted-foreground" data-id="element-2132">
                  <span data-id="element-2133">
                    {(syndicateData.totalCapital / syndicateData.targetCapital * 100).toFixed(0)}
                    % Funded
                  </span>
                  <span data-id="element-2134">
                    {formatCurrency(syndicateData.targetCapital - syndicateData.totalCapital)}{' '}
                    Remaining
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-2135">
            <CardHeader data-id="element-2136">
              <CardTitle data-id="element-2137">Your Share</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-2138">
              <div className="p-4 border rounded-md bg-muted/30" data-id="element-2139">
                <div className="flex items-center justify-between mb-2" data-id="element-2140">
                  <h3 className="text-sm font-medium" data-id="element-2141">Estimated Share</h3>
                  <div className="flex items-center" data-id="element-2142">
                    <CalculatorIcon className="h-4 w-4 mr-1 text-muted-foreground" data-id="element-2143" />
                    <span className="text-sm text-muted-foreground" data-id="element-2144">
                      Calculator
                    </span>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-2145">
                  <div className="flex justify-between items-end" data-id="element-2146">
                    <span className="text-sm text-muted-foreground" data-id="element-2147">
                      Your Contribution
                    </span>
                    <span className="text-xl font-bold" data-id="element-2148">
                      {formatCurrency(contributionAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between items-end" data-id="element-2149">
                    <span className="text-sm text-muted-foreground" data-id="element-2150">
                      After This Contribution
                    </span>
                    <span className="text-lg font-medium" data-id="element-2151">
                      {formatCurrency(syndicateData.totalCapital + parseFloat(contributionAmount))}
                    </span>
                  </div>
                  <div className="flex justify-between items-end" data-id="element-2152">
                    <span className="text-sm text-muted-foreground" data-id="element-2153">
                      Your Share Percentage
                    </span>
                    <div className="flex items-center" data-id="element-2154">
                      <PercentIcon className="h-4 w-4 mr-1 text-primary" data-id="element-2155" />
                      <span className="text-xl font-bold" data-id="element-2156">
                        {calculateSharePercentage()}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-2157">
                <h3 className="text-sm font-medium mb-2" data-id="element-2158">
                  Estimated Allocation
                </h3>
                <div className="space-y-2" data-id="element-2159">
                  {syndicateData.allocation.map((item, index) => <div key={index} className="flex justify-between text-sm" data-id="element-2160">
                      <span className="text-muted-foreground" data-id="element-2161">{item.type}</span>
                      <span data-id="element-2162">{calculateAllocation(item.percentage)}%</span>
                    </div>)}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-2163">
            <CardHeader data-id="element-2164">
              <CardTitle data-id="element-2165">Escrow Protection</CardTitle>
            </CardHeader>
            <CardContent data-id="element-2166">
              <div className="flex items-start" data-id="element-2167">
                <ShieldIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-2168" />
                <div data-id="element-2169">
                  <p className="text-sm font-medium" data-id="element-2170">100% Secure Escrow</p>
                  <p className="text-sm text-muted-foreground mt-1" data-id="element-2171">
                    Your funds are held in a secure escrow account until the
                    syndicate reaches its target. If the syndicate fails to
                    reach its target by the end date, you will receive a full
                    refund of your contribution minus any applicable fees.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
};
export default SyndicateContribution;